import React, { useEffect, useRef, useState } from 'react';
import { computeCompositionFitBox } from '@/lib/layers/computeCompositionFitBox';

interface Props {
  /** Composition width/height ratio (e.g. 1080/1920 for 9:16). */
  compAspect: number;
  children: React.ReactNode;
}

/** Positions its children over the exact area where the Remotion player
 *  draws the composition inside the preview wrapper ("contain" fit,
 *  centered). Rect fractions inside are then relative to the composition,
 *  matching the render — the canvas selection boxes hug the media. */
export const CompositionFitOverlay: React.FC<Props> = ({ compAspect, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  useEffect(() => {
    const parent = ref.current?.parentElement;
    if (!parent) return;
    const update = () => {
      const r = parent.getBoundingClientRect();
      setBox(computeCompositionFitBox({
        containerWidth: r.width,
        containerHeight: r.height,
        compAspect,
      }));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [compAspect]);

  return (
    <div
      ref={ref}
      className="absolute z-20 pointer-events-none"
      style={box ? { left: box.left, top: box.top, width: box.width, height: box.height } : { inset: 0 }}
    >
      {children}
    </div>
  );
};

export default CompositionFitOverlay;
